import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { useContext, useState } from 'react'
import { DatePicker, CRMSearch } from '../helper'
import dayjs from 'dayjs'
import {
  contractNumbers,
  contractVehicles,
  coreCompetencies,
  govPayTerms,
  quoteStatuses,
  salesforceForecastCategories,
  salesforceStages,
  setAsides,
} from '../lists'
import './NewQuote.css'
import { dataContext } from '..'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { quoteType, salesforceOpportunityType } from '../types'

export default function NewQuoteDialog(props: { open: boolean; onClose: () => void }) {
  const { user, repCodes } = useContext(dataContext)
  const navigate = useNavigate()

  const [newQuote, setNewQuote] = useState<Partial<quoteType>>({
    RepCode: '',
    Name: '',
    RFQNumber: '',
    ContractVehicle: '',
    ContractNumber: '',
    ExpirationDate: dayjs().add(30, 'day').toDate() as Date | null,
    RFQDueDate: null as Date | null,
    QuoteDescription: '',
    GovPayTerms: '',
    Status: 'Draft',
    QuoteDate: dayjs().toDate(),
  })
  const [existingOpportunity, setExistingOpportunity] = useState(true)
  const [selectedOpportunity, setSelectedOpportunity] = useState<{ id: string; label: string } | null>(null)
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; label: string } | null>(null)
  const [selectedContact, setSelectedContact] = useState<{ id: string; label: string } | null>(null)
  const [newOpportunity, setNewOpportunity] = useState<Partial<salesforceOpportunityType>>({
    Name: '',
    CloseDate: null,
    StageName: '',
    ForecastCategoryName: '',
    Line_Of_Business__c: [],
    Set_Asides__c: [],
  })
  const [loading, setLoading] = useState(false)
  const [fieldMissingError, setFieldMissingError] = useState({
    repCode: false,
    contractVehicle: false,
    id: false,
    name: false,
    closeDate: false,
    stageName: false,
    forecastCategory: false,
    coreCompetency: false,
    setAsides: false,
    accountId: false,
    contactId: false,
  })
  const [createOppError, setCreateOppError] = useState(false)
  const [createQuoteError, setCreateQuoteError] = useState(false)
  const [createSalesforceQuoteError, setCreateSalesforceQuoteError] = useState(false)

  const handleClose = () => {
    props.onClose()
    setNewQuote({
      RepCode: '',
      ContractVehicle: '',
      ContractNumber: '',
      ExpirationDate: dayjs().add(30, 'day').toDate(),
      RFQNumber: '',
      RFQDueDate: null,
      QuoteDescription: '',
      GovPayTerms: '',
      Status: 'Draft',
      QuoteDate: dayjs().toDate(),
      Name: '',
    })
    setExistingOpportunity(true)
    setSelectedOpportunity(null)
    setSelectedAccount(null)
    setSelectedContact(null)
    setNewOpportunity({
      Name: '',
      CloseDate: null,
      StageName: '',
      ForecastCategoryName: '',
      Line_Of_Business__c: [],
      Set_Asides__c: [],
    })
    setFieldMissingError({
      repCode: false,
      contractVehicle: false,
      id: false,
      name: false,
      closeDate: false,
      stageName: false,
      forecastCategory: false,
      coreCompetency: false,
      setAsides: false,
      accountId: false,
      contactId: false,
    })
    setCreateOppError(false)
    setCreateQuoteError(false)
    setCreateSalesforceQuoteError(false)
  }

  const handleCreateNewQuote = () => {
    const missingFields = {
      repCode: !newQuote.RepCode,
      contractVehicle: !newQuote.ContractVehicle,
      id: !selectedOpportunity,
      name: !newOpportunity.Name,
      closeDate: !newOpportunity.CloseDate,
      stageName: !newOpportunity.StageName,
      forecastCategory: !newOpportunity.ForecastCategoryName,
      coreCompetency: newOpportunity.Line_Of_Business__c?.length === 0,
      setAsides: newOpportunity.Set_Asides__c?.length === 0,
      accountId: !selectedAccount,
      contactId: !selectedContact,
    }
    setFieldMissingError(missingFields)

    if (
      !newQuote.RepCode ||
      !newQuote.ContractVehicle ||
      (existingOpportunity && !selectedOpportunity) ||
      (!existingOpportunity &&
        (missingFields.name ||
          missingFields.closeDate ||
          missingFields.stageName ||
          missingFields.forecastCategory ||
          missingFields.coreCompetency ||
          missingFields.setAsides ||
          missingFields.accountId ||
          missingFields.contactId))
    ) {
      return
    }

    setLoading(true)
    setCreateOppError(false)
    setCreateQuoteError(false)
    setCreateSalesforceQuoteError(false)
    axios
      .post('/newQuote', {
        quote: {
          ...newQuote,
          CRMOpportunity: existingOpportunity ? selectedOpportunity?.id : null,
        },
        newOpportunity: existingOpportunity
          ? undefined
          : { ...newOpportunity, AccountId: selectedAccount?.id, Owner: { Email: user.email } },
        salesforceQuote: {
          ContactId: selectedContact?.id,
        },
      })
      .then(res => {
        handleClose()
        navigate(`/quote/${res.data}`)
      })
      .catch(err => {
        console.error(err)
        if (err.response.data.message === 'Failed to create opportunity') {
          setCreateOppError(true)
        } else if (err.response.data.message === 'Failed to create quote') {
          setCreateQuoteError(true)
          if (!existingOpportunity) {
            setExistingOpportunity(true)
            setSelectedOpportunity({
              id: err.response.data.opportunityId,
              label: newOpportunity.Name ?? 'New Opportunity ' + dayjs().format('MM/DD/YYYY'),
            })
          }
        } else if (err.response.data.message === 'Failed to create Salesforce quote') {
          setCreateSalesforceQuoteError(true)
          handleClose()
          navigate(`/quote/${err.response.data.quoteId}`)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog fullWidth open={props.open} onClose={handleClose} maxWidth="lg">
        <DialogTitle>New Quote</DialogTitle>
        <DialogContent className="new-quote-dialog-content">
          <Typography>Fields marked with a * are required.</Typography>
          <Typography variant="subtitle2" fontWeight="bold">
            Main Quote Information
          </Typography>
          <Grid container spacing="16px" width="100%">
            <Grid size={6}>
              <TextField
                fullWidth
                label="Quote Name"
                size="small"
                value={newQuote.Name}
                onChange={e => setNewQuote(prev => ({ ...prev, Name: e.target.value }))}
                slotProps={{ htmlInput: { maxLength: 250 } }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Quote Description"
                size="small"
                value={newQuote.QuoteDescription}
                onChange={e => setNewQuote(prev => ({ ...prev, QuoteDescription: e.target.value }))}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                select
                label="Contract Vehicle *"
                size="small"
                value={newQuote.ContractVehicle}
                onChange={e => setNewQuote(prev => ({ ...prev, ContractVehicle: e.target.value }))}
                error={fieldMissingError.contractVehicle}
                helperText={fieldMissingError.contractVehicle ? 'Contract vehicle is required' : ''}
              >
                {contractVehicles.map(vehicle => (
                  <MenuItem value={vehicle} key={vehicle}>
                    {vehicle}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <DatePicker
                fullWidth
                label="Expiration Date"
                size="small"
                value={newQuote.ExpirationDate ? dayjs(newQuote.ExpirationDate) : null}
                onChange={e => setNewQuote(prev => ({ ...prev, ExpirationDate: e ? dayjs(e).toDate() : null }))}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                select
                label="Terms"
                size="small"
                value={newQuote.GovPayTerms}
                onChange={e => setNewQuote(prev => ({ ...prev, GovPayTerms: e.target.value }))}
              >
                {govPayTerms.map(term => (
                  <MenuItem value={term} key={term}>
                    {term}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                label="Status"
                size="small"
                value={newQuote.Status}
                onChange={e => setNewQuote(prev => ({ ...prev, Status: e.target.value }))}
              >
                {quoteStatuses.map(status => (
                  <MenuItem value={status} key={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <DatePicker
                fullWidth
                label="Quote Date"
                size="small"
                value={newQuote.QuoteDate ? dayjs(newQuote.QuoteDate) : null}
                onChange={e => setNewQuote(prev => ({ ...prev, QuoteDate: e ? dayjs(e).toDate() : null }))}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                select
                label="Rep Code *"
                size="small"
                value={newQuote.RepCode}
                onChange={e => setNewQuote(prev => ({ ...prev, RepCode: e.target.value }))}
                error={fieldMissingError.repCode}
                helperText={fieldMissingError.repCode ? 'Rep code is required' : ''}
              >
                {repCodes
                  .filter(code => code.IsrEmail === user.email)
                  .map(code => (
                    <MenuItem value={code.RepCode} key={code.RepCode}>
                      {code.RepCode} {[code.IsrName, code.AeName, code.EngineerName].filter(name => !!name).join(', ')}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
          </Grid>
          <Typography variant="subtitle2" fontWeight="bold">
            Additional Quote Information
          </Typography>
          <Grid container spacing="16px" width="100%">
            <Grid size={6}>
              <DatePicker
                fullWidth
                label="RFQ Due Date"
                size="small"
                value={newQuote.RFQDueDate ? dayjs(newQuote.RFQDueDate) : null}
                onChange={e => setNewQuote(prev => ({ ...prev, RFQDueDate: e ? dayjs(e).toDate() : null }))}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="RFQ #"
                size="small"
                value={newQuote.RFQNumber}
                onChange={e => setNewQuote(prev => ({ ...prev, RFQNumber: e.target.value }))}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                select
                label="Contract #"
                size="small"
                value={newQuote.ContractNumber}
                onChange={e => setNewQuote(prev => ({ ...prev, ContractNumber: e.target.value }))}
              >
                {contractNumbers.map(number => (
                  <MenuItem value={number} key={number}>
                    {number}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <div className="opportunity-container">
            <div>
              <Typography>Is there an existing opportunity?</Typography>
              <RadioGroup
                row
                value={existingOpportunity}
                onChange={e => setExistingOpportunity(e.target.value === 'true')}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </div>
            {!existingOpportunity && (
              <Typography color="text.secondary" variant="body2">
                Clicking the create button will create a new opportunity in the CRM
              </Typography>
            )}
          </div>
          {existingOpportunity ? (
            <CRMSearch
              object="Opportunity"
              label="Opportunity Name"
              onChange={(e, value) => setSelectedOpportunity(value)}
              value={selectedOpportunity}
              error={fieldMissingError.id}
              errorText="Opportunity is required"
            />
          ) : (
            <Grid container spacing="16px" width="100%">
              <Grid size={6}>
                <TextField
                  fullWidth
                  error={fieldMissingError.name}
                  helperText={fieldMissingError.name ? 'Opportunity is required' : ''}
                  label="Opportunity Name"
                  size="small"
                  value={newOpportunity.Name}
                  onChange={e => setNewOpportunity(prev => ({ ...prev, Name: e.target.value }))}
                />
              </Grid>
              <Grid size={6}>
                <DatePicker
                  fullWidth
                  error={fieldMissingError.closeDate}
                  helperText={fieldMissingError.closeDate ? 'Close Date is required' : ''}
                  label="Close Date"
                  size="small"
                  value={newOpportunity.CloseDate ? dayjs(newOpportunity.CloseDate) : null}
                  onChange={e => setNewOpportunity(prev => ({ ...prev, CloseDate: e ? dayjs(e).toDate() : null }))}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  select
                  error={fieldMissingError.stageName}
                  helperText={fieldMissingError.stageName ? 'Stage is required' : ''}
                  label="Stage"
                  size="small"
                  value={newOpportunity.StageName}
                  onChange={e => setNewOpportunity(prev => ({ ...prev, StageName: e.target.value }))}
                >
                  {salesforceStages.map(stage => (
                    <MenuItem value={stage} key={stage}>
                      {stage}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  select
                  label="Forecast Category"
                  size="small"
                  value={newOpportunity.ForecastCategoryName}
                  onChange={e => setNewOpportunity(prev => ({ ...prev, ForecastCategoryName: e.target.value }))}
                  error={fieldMissingError.forecastCategory}
                  helperText={fieldMissingError.forecastCategory ? 'Forecast Category is required' : ''}
                >
                  {salesforceForecastCategories.map(category => (
                    <MenuItem value={category} key={category}>
                      {category}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  select
                  error={fieldMissingError.coreCompetency}
                  helperText={fieldMissingError.coreCompetency ? 'Core Competency is required' : ''}
                  label="Core Competency"
                  size="small"
                  slotProps={{ select: { multiple: true } }}
                  value={newOpportunity.Line_Of_Business__c}
                  onChange={e =>
                    setNewOpportunity(prev => ({
                      ...prev,
                      Line_Of_Business__c:
                        typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                    }))
                  }
                >
                  {coreCompetencies.map(competency => (
                    <MenuItem value={competency} key={competency}>
                      {competency}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  select
                  error={fieldMissingError.setAsides}
                  helperText={fieldMissingError.setAsides ? 'Set Asides is required' : ''}
                  label="Set Asides"
                  size="small"
                  slotProps={{ select: { multiple: true } }}
                  value={newOpportunity.Set_Asides__c}
                  onChange={e =>
                    setNewOpportunity(prev => ({
                      ...prev,
                      Set_Asides__c: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                    }))
                  }
                >
                  {setAsides.map(aside => (
                    <MenuItem value={aside} key={aside}>
                      {aside}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={6}>
                <CRMSearch
                  object="Account"
                  label="CRM Account"
                  onChange={(e, value) => setSelectedAccount(value)}
                  value={selectedAccount}
                  error={fieldMissingError.accountId}
                  errorText="Account is required"
                />
              </Grid>
              <Grid size={6}>
                <CRMSearch
                  object="Contact"
                  label="Customer Contact"
                  accountId={selectedAccount?.id}
                  onChange={(e, value) => setSelectedContact(value)}
                  value={selectedContact}
                  error={fieldMissingError.contactId}
                  errorText="Contact is required"
                />
              </Grid>
            </Grid>
          )}
          <Snackbar open={createOppError}>
            <Alert severity="error" variant="filled" onClose={() => setCreateOppError(false)}>
              Failed to create opportunity. Please try again or contact support if the problem persists.
            </Alert>
          </Snackbar>
          <Snackbar open={createQuoteError}>
            <Alert severity="error" variant="filled" onClose={() => setCreateQuoteError(false)}>
              Failed to create quote. Please try again or contact support if the problem persists.
            </Alert>
          </Snackbar>
        </DialogContent>
        <DialogActions className="new-quote-dialog-actions">
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateNewQuote} loading={loading}>
            Create New Quote
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={createSalesforceQuoteError}>
        <Alert severity="error" variant="filled" onClose={() => setCreateSalesforceQuoteError(false)}>
          Failed to sync quote to Salesforce. Please try again by clicking the Sync Quote button or contact support if
          the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}
